import { Image } from "@/components/Image";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  AlertTriangle,
  ChevronDown,
  Clock,
  ImagePlus,
  Link2,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import { visibleErrorText } from "@/lib/errorText";
import PlatformGlyph from "@/app/routes/dashboard/components/PlatformGlyph";
import { queue } from "@/components/Toast";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitSegmentedControl } from "@/app/routes/dashboard/components/kit/SegmentedControl";
import {
  brandLocalToUtc,
  dstWallClockStatus,
  MIN_LEAD_MS,
  toBrandLocal,
} from "@/lib/brandTime";
import {
  type CalendarPlanSlot,
  type CalendarPlatform,
} from "@/types/contentCalendar.type";
import type { ProductionBrief } from "@/types/productionBrief.type";
import {
  PLATFORM_ACCOUNT_TYPE,
  PLATFORM_FIELD_LIMITS,
  PUBLICATION_ISSUE_HELP,
  PUBLICATION_STATUS_LABEL,
  distributionFiles,
  distributionPublications,
  isBlockingIssue,
  renderSnapchatDescription,
  type CoverLockReason,
  type DistributionResult,
  type Publication,
  type PublicationFields,
  type PublishableAccount,
  type SkippedChannel,
  type SlotFile,
} from "@/types/scheduling.type";
import { PLATFORM_META, PlatformPicker } from "./PlatformPicker";
import { CUSTOM_THUMBNAIL_ID, ThumbnailPicker } from "./ThumbnailPicker";
import WorkflowTag, { publicationWorkflowState } from "./WorkflowTag";
import { FILE_TARGET_OPTIONS, UploadZone, thumbnailExtension, type FileTarget } from "./UploadZones";
import { intendedPublicationChannels } from "../publicationIntegrity";
import { useUpdatePlanSlot } from "../api/useContentCalendar";
import { putToPresignedUrl } from "../api/putToPresignedUrl";
import { captureVideoThumbnails, type ThumbnailCandidate } from "../videoThumbnails";
import { coverLockMessage, fileReachesYouTube, YOUTUBE_VERIFY_URL, youtubeCoverProblem } from "../coverRules";
import {
  isPendingSlotFile,
  useDeleteSlotFile,
  useDeliverableThumbnailUploadUrl,
  useDistribution,
  useMaterializePublications,
  usePublishableAccounts,
  useRetryPublication,
  useSchedulePublication,
  useSetDeliverableThumbnail,
  useUnschedulePublication,
  useUpdatePublication,
} from "../api/useScheduling";

/**
 * The scheduling stage: the post's files (one for every platform, and any per
 * platform), then one card per platform with the file it uses, its prefilled
 * copy, a brand-timezone publish time, and the schedule controls. Unlocked by
 * the server's upload guard: an ACCEPTED production brief, or a DIRECT quick
 * post, which has no brief to wait on at all — `brief: null` is exactly that case.
 *
 * Field limits come from PLATFORM_FIELD_LIMITS, the web mirror of the backend
 * capability registry — the server still re-validates; the counters just keep
 * the user from typing past the line.
 */

function fmtBytes(n: number): string {
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GB`;
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** Publication evidence is shown in the brand's operating clock, not the
 * browser's — an operator travelling should still see the time the brand ran. */
function fmtBrandTimestamp(iso: string, timezone: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "Time unavailable";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(instant);
  } catch {
    // An invalid brand timezone is already called out by the settings guard.
    // Keep the evidence readable in UTC rather than dropping the timestamp.
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(instant);
  }
}

/** "Every platform", or the one platform a file was uploaded for. */
function fileTargetLabel(file: Pick<SlotFile, "forPlatform">): string {
  return file.forPlatform ? PLATFORM_META[file.forPlatform].label : "Every platform";
}

function publicationStatusLabel(publication: Publication): string {
  if (publication.status === "PUBLISHED") return "Live";
  return publication.status === "FAILED" && publication.nextAttemptAt
    ? "Retry scheduled"
    : PUBLICATION_STATUS_LABEL[publication.status];
}

function PublicationCounts({
  pending,
  inFlight,
  retrying,
  scheduled,
  live,
  failed,
  blocked,
  manual,
}: {
  pending: number;
  inFlight: number;
  retrying: number;
  scheduled: number;
  live: number;
  failed: number;
  blocked: number;
  manual: number;
}) {
  const total = pending + inFlight + retrying + scheduled + live + failed + blocked + manual;
  const queued = pending + inFlight + retrying + scheduled;
  const needsAction = failed + blocked + manual;
  const operationalIssues = failed + blocked;
  const items = [
    { label: "Destinations", value: total, detail: `${total === 1 ? "channel" : "channels"} in plan` },
    { label: "In motion", value: queued, detail: "draft, queued or scheduled" },
    { label: "Live", value: live, detail: live === 1 ? "channel published" : "channels published", tone: live > 0 ? "text-[var(--co-live)]" : undefined },
    {
      label: "Needs action",
      value: needsAction,
      detail:
        needsAction === 0
          ? "no exceptions"
          : `${operationalIssues} blocked · ${manual} manual`,
      tone: needsAction > 0 ? "text-co-attention" : undefined,
    },
  ];
  return (
    <dl
      aria-label="Publication channel summary"
      className="grid grid-cols-2 border-y border-co-line2 bg-co-surface lg:grid-cols-4"
    >
      {items.map((item) => (
        <div
          key={item.label}
          className="min-w-0 border-b border-r border-co-line px-4 py-3 last:border-r-0 even:border-r-0 [&:nth-last-child(-n+2)]:border-b-0 lg:border-b-0 lg:even:border-r lg:[&:nth-child(4)]:border-r-0"
        >
          <dt className="tt-label uppercase text-co-faint">{item.label}</dt>
          <dd className="mt-1 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
            <span className={cn("font-section text-[24px] font-extrabold leading-none tabular-nums text-co-text", item.tone)}>
              {item.value}
            </span>
            <span className="tt-read-s leading-tight text-co-muted">{item.detail}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SectionHeading({
  id,
  step,
  title,
  description,
  action,
}: {
  id?: string;
  step: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-co-line2 pb-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="tt-label uppercase text-co-accent">Step {step}</span>
          <h4 id={id} className="tt-title-l font-extrabold uppercase text-co-text">{title}</h4>
        </div>
        <p className="tt-read-m mt-1 max-w-[68ch] text-co-muted">{description}</p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

// ---- One publication card ----

function TokenField({
  label,
  values,
  draft,
  onDraftChange,
  onValuesChange,
  disabled,
  disabledMessage,
  hue,
  hashtags = false,
  maxTokens,
  maxCharacters,
}: {
  label: string;
  values: string[];
  draft: string;
  onDraftChange: (value: string) => void;
  onValuesChange: (value: string[]) => void;
  disabled: boolean;
  disabledMessage: string;
  hue?: string;
  hashtags?: boolean;
  maxTokens?: number;
  maxCharacters?: number;
}) {
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const characterCount = values.reduce((total, value) => total + value.length, 0);
  const atTokenLimit = maxTokens !== undefined && values.length >= maxTokens;

  const addTokens = (raw: string) => {
    const candidates = (hashtags ? raw.split(/[\s,]+/) : raw.split(/[,\n]+/))
      .map((value) => value.trim().replace(/^#+/, ""))
      .filter(Boolean);
    if (candidates.length === 0) {
      onDraftChange("");
      return;
    }

    const next = [...values];
    const seen = new Set(next.map((value) => value.toLocaleLowerCase()));
    let hitTokenLimit = false;
    let hitCharacterLimit = false;

    for (const candidate of candidates) {
      const key = candidate.toLocaleLowerCase();
      if (seen.has(key)) continue;
      if (maxTokens !== undefined && next.length >= maxTokens) {
        hitTokenLimit = true;
        continue;
      }
      const nextCharacterCount = next.reduce((total, value) => total + value.length, 0) + candidate.length;
      if (maxCharacters !== undefined && nextCharacterCount > maxCharacters) {
        hitCharacterLimit = true;
        continue;
      }
      next.push(candidate);
      seen.add(key);
    }

    if (next.length !== values.length) onValuesChange(next);
    onDraftChange("");
    setLimitMessage(
      hitTokenLimit
        ? `${maxTokens} hashtags is the platform limit.`
        : hitCharacterLimit
          ? `Tags can use up to ${maxCharacters} characters.`
          : null,
    );
  };

  const removeToken = (index: number) => {
    onValuesChange(values.filter((_, tokenIndex) => tokenIndex !== index));
    setLimitMessage(null);
  };

  const instructions = hashtags
    ? "Type a hashtag, then press Space or Enter."
    : "Type a tag, then press Enter or comma.";
  const countLabel = maxTokens !== undefined
    ? `${values.length}/${maxTokens} hashtags`
    : maxCharacters !== undefined
      ? `${characterCount}/${maxCharacters} characters`
      : `${values.length} ${values.length === 1 ? "tag" : "tags"}`;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="tt-label uppercase text-co-faint">{label}</span>
      <div
        className={cn(
          "flex min-h-11 flex-wrap items-center gap-2 border border-co-line bg-co-surface-solid px-2.5 py-2 transition has-[input:focus]:border-co-accent",
          disabled && "bg-co-recess",
        )}
      >
        {values.map((value, index) => (
          <span
            key={`${value}-${index}`}
            data-tone="quiet"
            className={cn(
              "tt-read-s inline-flex min-h-8 max-w-full items-center gap-1 rounded-none border border-co-line2 bg-co-recess text-co-text2",
              disabled ? "px-2.5" : "pl-2.5 pr-0.5",
              hue && "co-pillar-identity co-pillar-pill",
            )}
            style={hue ? ({ "--co-pillar-hue": hue } as CSSProperties) : undefined}
          >
            <span className="flex min-w-0 items-baseline gap-0.5">
              {hashtags ? (
                <span aria-hidden="true" className={cn("shrink-0", hue && "co-pillar-glyph")}>#</span>
              ) : null}
              <span className="truncate">{value}</span>
            </span>
            {!disabled ? (
              <button
                type="button"
                onClick={() => removeToken(index)}
                aria-label={`Remove ${hashtags ? "hashtag" : "tag"} ${value}`}
                className="grid size-7 shrink-0 place-items-center rounded-none text-co-faint transition hover:bg-co-surface hover:text-co-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-co-accent"
              >
                <X aria-hidden className="size-3" />
              </button>
            ) : null}
          </span>
        ))}
        {!disabled ? (
          <input
            value={draft}
            onChange={(event) => {
              const nextDraft = event.target.value;
              const hasDelimiter = hashtags ? /[\s,]/.test(nextDraft) : /[,\n]/.test(nextDraft);
              if (hasDelimiter) addTokens(nextDraft);
              else onDraftChange(nextDraft);
            }}
            onBlur={() => {
              if (draft.trim()) addTokens(draft);
            }}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData("text");
              const hasDelimiter = hashtags ? /[\s,]/.test(pasted) : /[,\n]/.test(pasted);
              if (!hasDelimiter) return;
              event.preventDefault();
              addTokens(`${draft}${draft ? (hashtags ? " " : ",") : ""}${pasted}`);
            }}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !draft && values.length > 0) {
                event.preventDefault();
                removeToken(values.length - 1);
                return;
              }
              const commitsToken =
                event.key === "Enter" || event.key === "," || (hashtags && event.key === " ");
              if (!commitsToken) return;
              event.preventDefault();
              if (draft.trim()) addTokens(draft);
            }}
            aria-label={`Add ${hashtags ? "hashtag" : "tag"}`}
            aria-invalid={Boolean(limitMessage)}
            placeholder={atTokenLimit ? "Limit reached" : hashtags ? "Add hashtag" : "Add tag"}
            autoCapitalize={hashtags ? "none" : undefined}
            autoCorrect={hashtags ? "off" : undefined}
            spellCheck={!hashtags}
            className="co-input-own-focus tt-read-s min-h-8 min-w-[8rem] flex-1 bg-transparent px-1 text-co-text shadow-none outline-none placeholder:text-co-faint"
          />
        ) : values.length === 0 ? (
          <span className="tt-read-s px-1 text-co-faint">None added</span>
        ) : null}
      </div>
      <span
        aria-live="polite"
        className={cn(
          "tt-label flex flex-wrap items-center justify-between gap-x-3 gap-y-1",
          limitMessage ? "text-red-500" : "text-co-faint",
        )}
      >
        <span>{disabled ? disabledMessage : limitMessage ?? instructions}</span>
        <span className="ml-auto tabular-nums">{countLabel}</span>
      </span>
    </div>
  );
}

function PublicationCard({
  publication,
  slot,
  timezone,
  timezoneReady,
  timezoneError,
  slotDay,
  accounts,
  accountsLoading,
  canEdit,
  pillarHue,
  files,
  coverLock,
}: {
  publication: Publication;
  slot: CalendarPlanSlot;
  timezone: string;
  timezoneReady: boolean;
  timezoneError: boolean;
  slotDay: string;
  accounts: PublishableAccount[];
  accountsLoading: boolean;
  canEdit: boolean;
  pillarHue?: string;
  /** Every file on the post, for the File select. */
  files: SlotFile[];
  /** Why the platform will keep its own frame instead of the post's cover. */
  coverLock: CoverLockReason | null;
}) {
  const meta = PLATFORM_META[publication.platform];
  const limits = PLATFORM_FIELD_LIMITS[publication.platform] ?? {};
  const local = timezoneReady ? toBrandLocal(publication.scheduledAt, timezone) : { day: "", time: "" };

  const [fields, setFields] = useState<PublicationFields>(publication.fields);
  const [day, setDay] = useState(local.day);
  const [time, setTime] = useState(local.time);
  const [tagDraft, setTagDraft] = useState("");
  const [hashtagDraft, setHashtagDraft] = useState("");

  const update = useUpdatePublication(slot.id);
  const schedule = useSchedulePublication(slot.id);
  const unschedule = useUnschedulePublication(slot.id);
  const retry = useRetryPublication(slot.id);
  const materialize = useMaterializePublications(slot.id);

  const timeDirty = day !== local.day || time !== local.time;
  const fieldsDirty = JSON.stringify(fields) !== JSON.stringify(publication.fields);
  const dirty = fieldsDirty || timeDirty;

  useEffect(() => {
    if (!timezoneReady) return;
    const next = toBrandLocal(publication.scheduledAt, timezone);
    if (!timeDirty) {
      setDay(next.day);
      setTime(next.time);
    }
  }, [timezone, timezoneReady, publication.scheduledAt, publication.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fieldsDirty) {
      setFields(publication.fields);
      setTagDraft("");
      setHashtagDraft("");
    }
  }, [publication.updatedAt, publication.fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const account = accounts.find((a) => a.accountId === publication.socialAccountId);
  const sameType = accounts.filter(
    (a) => a.type === (PLATFORM_ACCOUNT_TYPE[publication.platform] ?? a.type),
  );
  const editable =
    canEdit && ["DRAFT", "READY", "SCHEDULED", "FAILED"].includes(publication.status);
  const busy =
    update.isPending ||
    schedule.isPending ||
    unschedule.isPending ||
    retry.isPending ||
    materialize.isPending;
  const settingsBlocked = !timezoneReady || timezoneError;

  // The File select: only when there is a real choice of checked files. The
  // file in use stays listed even while it is not READY.
  const readyFiles = files.filter((file) => file.status === "READY" && !isPendingSlotFile(file));
  const fileOptions = readyFiles.some((file) => file.id === publication.deliverableId)
    ? readyFiles
    : [...files.filter((file) => file.id === publication.deliverableId), ...readyFiles];
  const fileEditable = canEdit && ["DRAFT", "READY", "FAILED"].includes(publication.status);

  // Which key the body and keyword list live in is a property of the platform,
  // not of whether it wants a title — Snapchat has a description and no title.
  const bodyField = limits.bodyField ?? (limits.requiresTitle ? "description" : "caption");
  const tagsField = limits.tagsField ?? (limits.requiresTitle ? "tags" : "hashtags");
  const usesTags = tagsField === "tags";

  const titleLen = (fields.title ?? "").length;
  const bodyMax = bodyField === "description" ? limits.descriptionMax : limits.captionMax;
  const bodyText = (fields[bodyField] ?? "") as string;
  // Snapchat renders the hashtags into the description, so the counter has to
  // measure what is actually sent or it would disagree with the server's issue.
  const bodyLen = limits.tagsCountTowardBody
    ? renderSnapchatDescription(bodyText, fields.hashtags ?? []).length
    : bodyText.length;

  const minLocal = timezoneReady
    // Wall-clock lead floor for the time picker; must read now at render.
    // eslint-disable-next-line react-hooks/purity
    ? toBrandLocal(new Date(Date.now() + MIN_LEAD_MS).toISOString(), timezone)
    : { day: "", time: "" };
  const dst =
    timezoneReady && day && time
      ? dstWallClockStatus(new Date(`${day}T00:00:00Z`), time, timezone)
      : "ok";
  const plannedDay = slotDay.slice(0, 10);
  const dayOffPlan = Boolean(day && plannedDay && day !== plannedDay);

  const surfaces = limits.surfaces ?? [];

  const save = () => {
    if (settingsBlocked) return;
    if (timeDirty && (!day || !time)) {
      queue.add({ title: "Pick a publish day and time", variant: "error" });
      return;
    }
    if (timeDirty) {
      const utc = brandLocalToUtc(new Date(`${day}T00:00:00Z`), time, timezone);
      if (utc.getTime() < Date.now() + MIN_LEAD_MS) {
        queue.add({ title: "Pick a time at least 5 minutes from now", variant: "error" });
        return;
      }
    }
    if (publication.status === "SCHEDULED") {
      const ok = window.confirm(
        "Saving will unschedule this post. You'll need to schedule it again after the edit.",
      );
      if (!ok) return;
    }
    update.mutate({
      publicationId: publication.id,
      input: {
        fields,
        ...(timeDirty ? { scheduledAt: { day, time } } : {}),
      },
    });
  };

  const canSchedule =
    publication.status === "READY" &&
    canEdit &&
    !busy &&
    !dirty &&
    !accountsLoading &&
    !!account?.canPublish &&
    account.connectionHealthy !== false;

  const attemptLabel = `${publication.attempts} ${
    publication.attempts === 1 ? "attempt" : "attempts"
  } made`;
  const nextRetryLabel = publication.nextAttemptAt
    ? `Automatic retry at ${fmtBrandTimestamp(publication.nextAttemptAt, timezone)}`
    : "No automatic retry scheduled";
  const retryScheduled = publication.status === "FAILED" && Boolean(publication.nextAttemptAt);
  const accountLabel = account?.name ?? publication.accountName ?? "Account not selected";
  const timingLabel = publication.publishedAt
    ? `Published ${fmtBrandTimestamp(publication.publishedAt, timezone)}`
    : publication.status === "PUBLISHED"
      ? "Published · time unavailable"
      : publication.status === "SCHEDULED"
        ? `Goes out ${day} ${time} ${timezone}`
        : publication.status === "READY"
          ? `Will go out ${day} ${time} ${timezone} once scheduled`
          : day && time
            ? `${day} ${time} ${timezone}`
            : "Publish time not set";
  const readOnlyMessage =
    publication.status === "PUBLISHED"
      ? "Locked after publication."
      : publication.status === "PUBLISHING"
        ? "Locked while the platform confirms the post."
        : "This field is read-only.";

  return (
    <section className="border border-co-line2 bg-co-surface">
      <header className="flex flex-col gap-3 border-b border-co-line2 bg-co-recess px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center border border-co-line2 bg-co-surface text-co-text">
            <PlatformGlyph platform={publication.platform} className="size-4.5" />
          </span>
          <div className="min-w-0">
            <h5 className="tt-title-m truncate font-extrabold uppercase text-co-text">{meta.label}</h5>
            <p className="tt-label truncate uppercase text-co-faint">{accountLabel}</p>
          </div>
        </div>
        <WorkflowTag state={publicationWorkflowState(publication.status, publication.nextAttemptAt)}>
          {publicationStatusLabel(publication)}
        </WorkflowTag>
      </header>

      <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(17rem,0.65fr)]">
        <div className="flex min-w-0 flex-col gap-5 p-4 sm:p-5 lg:p-6">
          <div>
            <h6 className="tt-section font-bold uppercase text-co-text">Channel copy</h6>
            <p className="tt-read-s mt-1 text-co-muted">Edit the words that will travel with this asset.</p>
          </div>

          {limits.requiresTitle ? (
            <label className="flex flex-col gap-1.5">
              <span className="tt-label uppercase text-co-faint">Title</span>
              <input
                value={fields.title ?? ""}
                disabled={!editable}
                maxLength={limits.titleMax}
                onChange={(e) => setFields((f) => ({ ...f, title: e.target.value }))}
                className="tt-read-s min-h-11 rounded-none border border-co-line bg-co-surface-solid px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
              />
              {limits.titleMax ? (
                <span className={cn("tt-label self-end", titleLen > limits.titleMax ? "text-red-500" : "text-co-faint")}>
                  {titleLen}/{limits.titleMax}
                </span>
              ) : null}
            </label>
          ) : null}

          <label className="flex flex-col gap-1.5">
            <span className="tt-label uppercase text-co-faint">
              {bodyField === "description" ? "Description" : "Caption"}
            </span>
            <textarea
              value={bodyText}
              disabled={!editable}
              // No maxLength when the hashtags share the budget: the browser would
              // cap the description alone and quietly hide the real overflow.
              maxLength={limits.tagsCountTowardBody ? undefined : bodyMax}
              onChange={(e) =>
                setFields((f) => ({ ...f, [bodyField]: e.target.value }))
              }
              rows={8}
              className="tt-read-m min-h-[220px] resize-y rounded-none border border-co-line bg-co-surface-solid px-3 py-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
            />
            {bodyMax ? (
              <span
                className={cn(
                  "tt-label self-end",
                  bodyLen > bodyMax ? "text-red-500" : "text-co-faint",
                )}
              >
                {bodyLen}/{bodyMax}
              </span>
            ) : null}
          </label>

          <TokenField
            label={usesTags ? "Tags" : "Hashtags"}
            values={usesTags ? (fields.tags ?? []) : (fields.hashtags ?? [])}
            draft={usesTags ? tagDraft : hashtagDraft}
            onDraftChange={usesTags ? setTagDraft : setHashtagDraft}
            onValuesChange={(values) =>
              setFields((current) =>
                usesTags
                  ? { ...current, tags: values }
                  : { ...current, hashtags: values },
              )
            }
            disabled={!editable}
            disabledMessage={readOnlyMessage}
            hue={pillarHue}
            hashtags={!usesTags}
            maxTokens={limits.maxHashtags}
            maxCharacters={limits.tagsMaxChars}
          />
        </div>

        <aside className="flex min-w-0 flex-col gap-5 border-t border-co-line2 bg-co-recess p-4 sm:p-5 lg:border-l lg:border-t-0 lg:p-6">
          <div>
            <h6 className="tt-section font-bold uppercase text-co-text">Release timing</h6>
            <p className="tt-read-s mt-1 text-co-muted">Choose the account and the exact brand-local time.</p>
          </div>

          {publication.status === "PUBLISHED" && (
            <div className="border-y border-[var(--co-live)]/35 bg-[var(--co-live)]/5 px-3 py-3">
              <span className="tt-label uppercase text-[var(--co-live)]">Live channel</span>
              <p className="tt-read-s mt-1 text-co-text2">
                Copy, account and timing are now read-only in Creative OS.
              </p>
            </div>
          )}
          {publication.status === "PUBLISHING" && (
            <div className="border-y border-co-accent-border bg-co-accent-dim px-3 py-3">
              <span className="tt-label uppercase text-co-accent">Publishing now</span>
              <p className="tt-read-s mt-1 text-co-text2">
                Editing is paused while the platform confirms the post.
              </p>
            </div>
          )}

          {readyFiles.length > 1 ? (
            <label className="flex flex-col gap-1.5">
              <span className="tt-label uppercase text-co-faint">File</span>
              <select
                value={publication.deliverableId}
                disabled={!fileEditable || update.isPending}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next || next === publication.deliverableId) return;
                  update.mutate({ publicationId: publication.id, input: { deliverableId: next } });
                }}
                className="tt-read-s min-h-11 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
              >
                {fileOptions.map((file) => (
                  <option key={file.id} value={file.id}>
                    {file.fileName} · {fileTargetLabel(file)}
                  </option>
                ))}
              </select>
              <span className="tt-read-s text-co-faint">
                {!fileEditable && canEdit
                  ? publication.status === "SCHEDULED"
                    ? "Unschedule to change the file."
                    : publication.status === "CANCELLED"
                      ? "Create the publication again to change its file."
                      : "Locked once the post goes out."
                  : publication.fileChosen
                    ? `Set for ${meta.label}.`
                    : "Uses the file for every platform."}
              </span>
            </label>
          ) : null}

          {sameType.length > 1 && editable ? (
            <label className="flex flex-col gap-1.5">
              <span className="tt-label uppercase text-co-faint">Account</span>
              <select
                value={publication.socialAccountId ?? ""}
                disabled={busy}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next || next === publication.socialAccountId) return;
                  if (publication.status === "SCHEDULED") {
                    const ok = window.confirm(
                      "Changing the account will unschedule this post. You'll need to schedule it again.",
                    );
                    if (!ok) return;
                  }
                  update.mutate({
                    publicationId: publication.id,
                    input: { socialAccountId: next },
                  });
                }}
                className="tt-read-s min-h-11 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
              >
                {sameType.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name ?? a.username ?? a.accountId}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="border-y border-co-line py-3">
              <span className="tt-label uppercase text-co-faint">Publishing account</span>
              <p className="tt-read-m mt-1 text-co-text2">{accountLabel}</p>
            </div>
          )}

          {surfaces.length > 1 && (
            <label className="flex flex-col gap-1.5">
              <span className="tt-label uppercase text-co-faint">Post as</span>
              <select
                value={fields.surface ?? surfaces[0]}
                disabled={!editable}
                onChange={(e) =>
                  setFields((f) => ({ ...f, surface: e.target.value as typeof surfaces[number] }))
                }
                className="tt-read-s min-h-11 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
              >
                {surfaces.map((surface) => (
                  <option key={surface} value={surface}>
                    {surface === "SPOTLIGHT" ? "Spotlight" : "Story"}
                  </option>
                ))}
              </select>
              <span className="tt-read-s text-co-faint">
                {(fields.surface ?? surfaces[0]) === "SPOTLIGHT"
                  ? "Permanent, video only, reviewed by Snapchat before it goes live."
                  : "Visible for 24 hours. Takes video or an image."}
              </span>
            </label>
          )}

          {!timezoneReady ? (
            <p className="tt-read-s text-co-faint">Loading the brand timezone…</p>
          ) : timezoneError ? (
            <p className="tt-read-s flex items-start gap-2 text-red-500">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              Could not load the brand timezone. Refresh before scheduling.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="tt-label uppercase text-co-faint">Publish day</span>
                <input
                  type="date"
                  value={day}
                  min={minLocal.day}
                  disabled={!editable}
                  onChange={(e) => setDay(e.target.value)}
                  className="tt-read-s min-h-11 min-w-0 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
                />
              </label>
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="tt-label uppercase text-co-faint">Time · {timezone}</span>
                <input
                  type="time"
                  value={time}
                  min={day === minLocal.day ? minLocal.time : undefined}
                  disabled={!editable}
                  onChange={(e) => setTime(e.target.value)}
                  className="tt-read-s min-h-11 min-w-0 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent disabled:opacity-60"
                />
              </label>
            </div>
          )}

          {account && !account.canPublish && (
            <p className="tt-read-s flex items-start gap-2 text-red-500">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              Reconnect {meta.label} in Settings to enable publishing.
            </p>
          )}
          {coverLock && editable && (
            <p className="tt-read-s flex items-start gap-2 text-amber-600">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {coverLockMessage(coverLock)}
                {coverLock === "CHANNEL_NOT_VERIFIED" ? (
                  <>
                    {" "}
                    <a href={YOUTUBE_VERIFY_URL} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
                      Verify on YouTube
                    </a>
                  </>
                ) : null}
              </span>
            </p>
          )}
          {dst === "nonexistent" && (
            <p className="tt-read-s flex items-start gap-2 text-amber-600">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              That time is skipped by daylight saving. Pick a different minute.
            </p>
          )}
          {dst === "ambiguous" && (
            <p className="tt-read-s flex items-start gap-2 text-amber-600">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              That time happens twice on this day. We’ll use the later occurrence.
            </p>
          )}
          {dayOffPlan && (
            <p className="tt-read-s flex items-start gap-2 text-amber-600">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              Publish day ({day}) differs from the planned day ({plannedDay}).
            </p>
          )}

          {/* Split by severity: blockers are why the row won't schedule, warnings
              are things worth knowing about a post that WILL go out. */}
          {(["BLOCK", "WARN"] as const).map((severity) => {
            const group = publication.issues.filter((issue) =>
              severity === "BLOCK" ? isBlockingIssue(issue) : !isBlockingIssue(issue),
            );
            if (group.length === 0) return null;
            const blocking = severity === "BLOCK";
            return (
              <ul
                key={severity}
                className={cn(
                  "flex flex-col gap-2 border px-3 py-3",
                  blocking ? "border-red-500/30 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5",
                )}
              >
                {group.map((issue, i) => (
                  <li
                    key={i}
                    className={cn(
                      "tt-read-s flex items-start gap-2",
                      blocking ? "text-red-500" : "text-amber-600",
                    )}
                  >
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      {issue.message}
                      {PUBLICATION_ISSUE_HELP[issue.code] ? (
                        <span className="mt-1 block opacity-80">{PUBLICATION_ISSUE_HELP[issue.code]}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            );
          })}

          {publication.status === "FAILED" && (
            <div
              role={retryScheduled ? "status" : "alert"}
              className={cn(
                "tt-read-s flex flex-col gap-2 border px-3 py-3",
                retryScheduled
                  ? "border-co-accent-border bg-co-accent-dim"
                  : "border-red-500/30 bg-red-500/5",
              )}
            >
              <p className={retryScheduled ? "text-co-text2" : "text-red-500"}>
                {visibleErrorText(publication.lastError) ?? "The platform refused the post."}
              </p>
              <p className="flex flex-wrap gap-x-2 gap-y-1 text-co-muted">
                <span>{attemptLabel}</span>
                <span aria-hidden="true">·</span>
                <span>{nextRetryLabel}</span>
              </p>
            </div>
          )}

          {publication.status === "READY" && (
            <p className="tt-read-s text-co-faint">Ready to schedule once the copy and time are saved.</p>
          )}
          {publication.status === "CANCELLED" && (
            <p className="tt-read-s text-co-muted">
              This publication was cancelled. Create it again before scheduling this channel.
            </p>
          )}
        </aside>
      </div>

      <footer className="flex flex-col gap-3 border-t border-co-line2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <span className="tt-label inline-flex min-w-0 items-start gap-2 uppercase text-co-faint">
          <Clock className="mt-0.5 size-3.5 shrink-0" />
          <span>{timingLabel}</span>
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {editable && dirty && (
            <button
              type="button"
              onClick={save}
              disabled={busy || settingsBlocked}
              className="tt-label inline-flex min-h-11 items-center border border-co-line2 px-3 uppercase text-co-text2 transition hover:border-co-accent disabled:opacity-50"
            >
              Save changes
            </button>
          )}
          {publication.status === "READY" && canEdit && (
            <button
              type="button"
              onClick={() => schedule.mutate(publication.id)}
              disabled={!canSchedule}
              title={
                dirty
                  ? "Save your edits first"
                  : !account?.canPublish || account.connectionHealthy === false
                    ? "Reconnect this account in Settings"
                    : accountsLoading
                      ? "Checking the connection…"
                      : undefined
              }
              className="tt-label inline-flex min-h-11 items-center bg-co-accent-fill px-4 uppercase text-co-on-accent transition hover:opacity-85 disabled:opacity-50"
            >
              Schedule post
            </button>
          )}
          {publication.status === "SCHEDULED" && canEdit && (
            <button
              type="button"
              onClick={() => unschedule.mutate(publication.id)}
              disabled={busy}
              className="tt-label inline-flex min-h-11 items-center border border-co-line2 px-3 uppercase text-co-text2 transition hover:border-co-accent disabled:opacity-50"
            >
              Unschedule
            </button>
          )}
          {publication.status === "FAILED" && canEdit && (
            <button
              type="button"
              onClick={() => retry.mutate(publication.id)}
              disabled={busy}
              className={cn(
                "tt-label inline-flex min-h-11 items-center gap-1.5 border px-3 uppercase transition hover:brightness-110 disabled:opacity-50",
                retryScheduled
                  ? "border-co-accent-border bg-co-accent-dim text-co-text2"
                  : "border-red-500/40 bg-red-500/10 text-red-500",
              )}
            >
              <RefreshCw className="size-3" />
              {publication.nextAttemptAt ? "Retry now" : "Retry"}
            </button>
          )}
          {publication.status === "CANCELLED" && canEdit && (
            <button
              type="button"
              onClick={() => materialize.mutate()}
              disabled={busy}
              className="tt-label inline-flex min-h-11 items-center border border-co-line2 px-3 uppercase text-co-text2 transition hover:border-co-accent disabled:opacity-50"
            >
              {materialize.isPending ? "Creating…" : "Create again"}
            </button>
          )}
          {publication.permalink && (
            <a
              href={publication.permalink}
              target="_blank"
              rel="noreferrer"
              className="tt-label inline-flex min-h-11 items-center gap-1.5 border border-co-line2 px-3 uppercase text-co-accent transition hover:border-co-accent"
            >
              <Link2 className="size-3" />
              View post
            </a>
          )}
          {publication.status === "PUBLISHED" && !publication.permalink && (
            <span
              title="The platform confirmed publication but did not return a public URL."
              className="tt-label inline-flex min-h-11 items-center gap-1.5 uppercase text-co-faint"
            >
              <Link2 className="size-3" />
              Public link unavailable
            </span>
          )}
        </div>
      </footer>
    </section>
  );
}

// ---- Skipped (non-publishable / unconnected) channels ----

function SkippedCard({
  skipped,
  slotId,
  canEdit,
  checking = false,
}: {
  skipped: SkippedChannel;
  slotId: string;
  canEdit: boolean;
  /** NEEDS_FANOUT while the platform's file is still being checked: its publication follows. */
  checking?: boolean;
}) {
  const meta = PLATFORM_META[skipped.platform];
  const materialize = useMaterializePublications(slotId);
  const manual = skipped.reason === "NOT_PUBLISHABLE";
  const waiting = skipped.reason === "NEEDS_FANOUT" && checking;
  return (
    <div className="grid min-h-[72px] grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-b border-co-line px-3 py-3 last:border-b-0 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:px-4">
      <span className="grid size-10 place-items-center border border-co-line2 bg-co-surface text-co-text2">
        <PlatformGlyph platform={skipped.platform} className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="tt-section block font-bold uppercase text-co-text">{meta.label}</span>
        <span className="tt-label mt-0.5 block uppercase text-co-faint">
          {manual
            ? "Post outside Creative OS"
            : waiting
              ? "Its file is being checked"
              : skipped.reason === "NEEDS_FANOUT"
                ? "Publication not created"
                : "Publishing account required"}
        </span>
      </span>
      {waiting ? (
        <WorkflowTag state="processing">Checking…</WorkflowTag>
      ) : skipped.reason === "NEEDS_FANOUT" ? (
        canEdit ? (
          <button
            type="button"
            onClick={() => materialize.mutate()}
            disabled={materialize.isPending}
            className="tt-label col-span-2 inline-flex min-h-11 items-center justify-center border border-co-line2 px-3 uppercase text-co-accent transition hover:border-co-accent disabled:opacity-50 sm:col-span-1"
          >
            {materialize.isPending ? "Creating…" : "Create publication"}
          </button>
        ) : (
          <span className="tt-read-s col-span-2 text-co-faint sm:col-span-1">Account connected — create a publication to schedule</span>
        )
      ) : manual ? (
        <WorkflowTag state="outstanding">Outstanding</WorkflowTag>
      ) : (
        <WorkflowTag state="blocked">Blocked</WorkflowTag>
      )}
    </div>
  );
}

/** A platform with no file of its own and no file for every platform: upload one for it here. */
function NeedsFileRow({
  platform,
  slot,
  canEdit,
}: {
  platform: CalendarPlatform;
  slot: CalendarPlanSlot;
  canEdit: boolean;
}) {
  const meta = PLATFORM_META[platform];
  const [open, setOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  return (
    <div className="border-b border-co-line last:border-b-0">
      <div className="grid min-h-[72px] grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 px-3 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:px-4">
        <span className="grid size-10 place-items-center border border-co-line2 bg-co-surface text-co-text2">
          <PlatformGlyph platform={platform} className="size-4" />
        </span>
        <span className="min-w-0">
          <span className="tt-section block font-bold uppercase text-co-text">{meta.label}</span>
          <span className="tt-label mt-0.5 block uppercase text-co-faint">Needs a file</span>
        </span>
        {canEdit ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            disabled={holding}
            className="tt-label col-span-2 inline-flex min-h-11 items-center justify-center border border-co-line2 px-3 uppercase text-co-accent transition hover:border-co-accent disabled:opacity-50 sm:col-span-1"
          >
            {open ? "Close" : `Upload for ${meta.label}`}
          </button>
        ) : (
          <WorkflowTag state="blocked">Needs a file</WorkflowTag>
        )}
      </div>
      {open && canEdit ? (
        <div className="px-3 pb-3 sm:px-4">
          <UploadZone
            slotId={slot.id}
            mediaType={slot.mediaType}
            forPlatform={platform}
            onHoldChange={setHolding}
            onConfirm={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

// ---- The post's files ----

/** Shaped like the file rows: a square thumbnail, the name and the meta line. */
function FilesSkeleton() {
  return (
    <div role="status" className="rounded-card border border-co-line2 bg-co-surface">
      <span className="sr-only">Loading the files</span>
      {[0, 1].map((row) => (
        <div key={row} aria-hidden="true" className="flex items-center gap-3 border-b border-co-line px-3 py-3 last:border-b-0 sm:px-4">
          <div className="size-14 shrink-0 animate-pulse rounded-card bg-co-hover motion-reduce:animate-none" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="h-3.5 w-[45%] animate-pulse rounded-tag bg-co-hover motion-reduce:animate-none" />
            <div className="h-3 w-[30%] animate-pulse rounded-tag bg-co-surface-solid motion-reduce:animate-none" />
          </div>
          <div className="hidden h-6 w-20 animate-pulse rounded-tag bg-co-surface-solid motion-reduce:animate-none sm:block" />
        </div>
      ))}
    </div>
  );
}

/**
 * One file on the post: what it is, who it is for, which platforms use it, and
 * its preview on demand. The signed URLs change on every read, so the row keeps
 * the first one it got (a new one only after a load failed, or a new cover).
 */
function FileRow({
  file,
  usedBy,
  canEdit,
  initiallyOpen,
  onRemove,
  onChangeThumbnail,
  onRefresh,
}: {
  file: SlotFile;
  usedBy: CalendarPlatform[];
  canEdit: boolean;
  initiallyOpen: boolean;
  onRemove: () => void;
  onChangeThumbnail: (mediaUrl: string | null) => void;
  onRefresh: () => void;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const [media, setMedia] = useState<{ url: string | null; failed: string | null }>({ url: file.mediaUrl, failed: null });
  const [thumb, setThumb] = useState<{ key: string | null; url: string | null; failed: string | null }>({
    key: file.thumbnailKey,
    url: file.thumbnailUrl,
    failed: null,
  });
  if (!media.url && file.mediaUrl && file.mediaUrl !== media.failed) {
    setMedia({ url: file.mediaUrl, failed: media.failed });
  }
  if (file.thumbnailUrl && file.thumbnailUrl !== thumb.failed && (!thumb.url || thumb.key !== file.thumbnailKey)) {
    setThumb({ key: file.thumbnailKey, url: file.thumbnailUrl, failed: thumb.failed });
  } else if (!file.thumbnailUrl && thumb.url) {
    setThumb({ key: null, url: null, failed: null });
  }
  const mediaFailed = () => {
    setMedia({ url: null, failed: media.url });
    onRefresh();
  };

  const pending = isPendingSlotFile(file);
  const checking = file.status === "UPLOADING" || file.status === "PROCESSING";
  const still = file.kind === "IMAGE" ? media.url : thumb.url;
  const metaLine = [
    file.kind === "VIDEO" ? "Video" : "Image",
    file.sizeBytes ? fmtBytes(file.sizeBytes) : null,
    file.width && file.height ? `${file.width} × ${file.height}` : null,
    file.durationSeconds ? `${Math.round(file.durationSeconds)} s` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="border-b border-co-line last:border-b-0">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-3 px-3 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:px-4">
        <span className="grid size-14 shrink-0 place-items-center overflow-hidden border border-co-line2 bg-co-recess">
          {still ? (
            <Image
              src={still}
              alt=""
              onError={
                file.kind === "IMAGE"
                  ? mediaFailed
                  : () => {
                      setThumb({ key: thumb.key, url: null, failed: thumb.url });
                      onRefresh();
                    }
              }
              className="size-full object-cover"
            />
          ) : checking ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin text-co-faint motion-reduce:animate-none" />
          ) : (
            <ImagePlus aria-hidden="true" className="size-4 text-co-faint" />
          )}
        </span>
        <div className="min-w-0">
          <p className="tt-read-m break-all font-medium text-co-text">{file.fileName}</p>
          <p className="tt-label mt-0.5 uppercase text-co-faint">
            {fileTargetLabel(file)}
            {file.isDefault && file.forPlatform === null ? " · Default" : ""}
            {metaLine ? ` · ${metaLine}` : ""}
          </p>
          <p className="tt-read-s mt-0.5 text-co-muted">
            {usedBy.length > 0
              ? `Used by ${usedBy.map((platform) => PLATFORM_META[platform].label).join(", ")}`
              : checking
                ? "Platforms use it once it is checked."
                : "No platform uses it."}
          </p>
        </div>
        <div className="col-span-2 flex flex-wrap items-center justify-end gap-2 sm:col-span-1">
          {checking ? (
            <WorkflowTag state={file.processingError ? "retrying" : "processing"}>
              {file.processingError ? "Retrying…" : "Checking…"}
            </WorkflowTag>
          ) : file.status === "FAILED" ? (
            <WorkflowTag state="failed">Failed</WorkflowTag>
          ) : (
            <WorkflowTag state="ready">Ready</WorkflowTag>
          )}
          {!pending ? (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
              className="tt-label inline-flex min-h-11 items-center gap-1.5 border border-co-line2 px-3 uppercase text-co-text2 transition hover:border-co-accent"
            >
              Preview
              <ChevronDown aria-hidden="true" className={cn("size-3.5 transition-transform", open && "rotate-180")} />
            </button>
          ) : null}
          {canEdit && !pending && file.kind === "VIDEO" ? (
            <button
              type="button"
              onClick={() => onChangeThumbnail(media.url ?? file.mediaUrl)}
              className="tt-label inline-flex min-h-11 items-center border border-co-line2 px-3 uppercase text-co-text2 transition hover:border-co-accent"
            >
              {thumb.url ? "Change cover" : "Add cover"}
            </button>
          ) : null}
          {canEdit && !pending ? (
            <button
              type="button"
              onClick={onRemove}
              title="Remove this file"
              aria-label={`Remove ${file.fileName}`}
              className="grid size-11 shrink-0 place-items-center border border-transparent text-co-faint transition hover:border-red-500/30 hover:text-red-500"
            >
              <Trash2 className="size-4" />
            </button>
          ) : null}
        </div>
      </div>

      {file.processingError && (file.status === "FAILED" || file.status === "PROCESSING") ? (
        <p
          role={file.status === "FAILED" ? "alert" : "status"}
          className={cn(
            "tt-read-s mx-3 mb-3 border-l-2 px-3 py-2 sm:mx-4",
            file.status === "FAILED"
              ? "border-red-500 bg-red-500/5 text-red-500"
              : "border-co-accent bg-co-accent-dim text-co-text2",
          )}
        >
          {file.processingError}
        </p>
      ) : null}

      {open && !pending ? (
        <div className="relative mx-3 mb-3 flex min-h-[260px] min-w-0 items-center justify-center bg-black/95 p-4 sm:mx-4 sm:min-h-[340px] sm:p-6">
          <span className="tt-label absolute left-4 top-4 uppercase text-white/55">Preview</span>
          {media.url ? (
            file.kind === "VIDEO" ? (
              <video
                src={media.url}
                controls
                preload="metadata"
                onError={mediaFailed}
                className="max-h-[420px] max-w-full object-contain"
              />
            ) : (
              <Image
                src={media.url}
                alt={file.fileName}
                onError={mediaFailed}
                className="max-h-[420px] max-w-full object-contain"
              />
            )
          ) : (
            <div className="flex max-w-sm flex-col items-center gap-3 px-4 text-center text-white/60">
              {checking ? (
                <Loader2 className="size-5 animate-spin motion-reduce:animate-none" />
              ) : (
                <ImagePlus className="size-5" />
              )}
              <p className="tt-read-s">
                {checking ? "The preview is being prepared." : "Preview unavailable. Refresh to request a new viewing link."}
              </p>
              {!checking ? (
                <button
                  type="button"
                  onClick={() => {
                    setMedia({ url: null, failed: null });
                    onRefresh();
                  }}
                  className="tt-label inline-flex min-h-11 items-center border border-white/30 px-3 uppercase text-white transition hover:border-white"
                >
                  Refresh preview
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Add a file: for every platform, or for one platform picked here. The choice
 * locks while the zone holds an upload, so switching cannot drop it.
 */
const ADD_PLATFORM_OPTION = "__add_platform__";

function AddFile({
  slot,
  channels,
  onAddPlatform,
}: {
  slot: CalendarPlanSlot;
  channels: CalendarPlatform[];
  onAddPlatform?: () => void;
}) {
  const [target, setTarget] = useState<FileTarget>("same");
  const [picked, setPicked] = useState<CalendarPlatform | null>(null);
  const [holding, setHolding] = useState(false);
  const platform = picked && channels.includes(picked) ? picked : (channels[0] ?? null);
  const perPlatform = target === "different" && !!platform;
  return (
    <div className="flex flex-col gap-3">
      {channels.length > 0 ? (
        <div inert={holding} className={cn("flex flex-col gap-3 sm:flex-row sm:items-end", holding && "opacity-60")}>
          <KitSegmentedControl
            ariaLabel="Which platforms the file is for"
            options={FILE_TARGET_OPTIONS}
            value={target}
            onChange={setTarget}
            className="min-w-0 flex-1 sm:max-w-[32rem]"
          />
          {target === "different" ? (
            <label className="flex min-w-[12rem] flex-col gap-1.5">
              <span className="tt-label uppercase text-co-faint">Platform</span>
              <select
                value={platform ?? ""}
                onChange={(e) => {
                  if (e.target.value === ADD_PLATFORM_OPTION) {
                    onAddPlatform?.();
                    return;
                  }
                  setPicked(e.target.value as CalendarPlatform);
                }}
                className="tt-read-s min-h-11 rounded-none border border-co-line bg-co-surface px-3 text-co-text outline-none transition focus:border-b-co-accent"
              >
                {channels.map((channel) => (
                  <option key={channel} value={channel}>
                    {PLATFORM_META[channel].label}
                  </option>
                ))}
                {onAddPlatform ? <option value={ADD_PLATFORM_OPTION}>+ Add a platform…</option> : null}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      <UploadZone
        key={perPlatform ? platform : "every"}
        slotId={slot.id}
        mediaType={slot.mediaType}
        forPlatform={perPlatform ? platform : null}
        channels={channels}
        onHoldChange={setHolding}
      />
    </div>
  );
}

// ---- Change / add thumbnail on an existing video ----

function ChangeThumbnailModal({
  slotId,
  deliverableId,
  mediaUrl,
  hasThumbnail,
  youtube,
  onClose,
}: {
  slotId: string;
  /** The video whose cover changes. */
  deliverableId: string;
  mediaUrl: string | null;
  hasThumbnail: boolean;
  /** The video posts to YouTube, so the cover must fit YouTube's limits. */
  youtube: boolean;
  onClose: () => void;
}) {
  const [candidates, setCandidates] = useState<ThumbnailCandidate[]>([]);
  const [captureState, setCaptureState] = useState<"capturing" | "done" | "unsupported">(
    mediaUrl ? "capturing" : "unsupported",
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [custom, setCustom] = useState<{ previewUrl: string; blob: Blob } | null>(null);
  const [saving, setSaving] = useState(false);
  const cancelledRef = useRef(false);
  const candidatesRef = useRef<ThumbnailCandidate[]>([]);
  const customPreviewRef = useRef<string | null>(null);

  const rememberCandidates = (frames: ThumbnailCandidate[]) => {
    candidatesRef.current = frames;
    setCandidates(frames);
  };
  const rememberCustom = (next: { previewUrl: string; blob: Blob } | null) => {
    customPreviewRef.current = next?.previewUrl ?? null;
    setCustom(next);
  };

  const thumbUploadUrl = useDeliverableThumbnailUploadUrl(slotId);
  const setThumbnail = useSetDeliverableThumbnail(slotId);

  useEffect(() => {
    cancelledRef.current = false;
    if (!mediaUrl) return;
    void captureVideoThumbnails(mediaUrl).then((frames) => {
      if (cancelledRef.current) {
        for (const c of frames) URL.revokeObjectURL(c.previewUrl);
        return;
      }
      rememberCandidates(frames);
      setCaptureState(frames.length === 0 ? "unsupported" : "done");
    });
    return () => {
      cancelledRef.current = true;
      for (const c of candidatesRef.current) URL.revokeObjectURL(c.previewUrl);
      if (customPreviewRef.current) URL.revokeObjectURL(customPreviewRef.current);
    };
  }, [mediaUrl]);

  const onCustomFile = (file: File) => {
    if (custom) URL.revokeObjectURL(custom.previewUrl);
    rememberCustom({ previewUrl: URL.createObjectURL(file), blob: file });
    setSelectedId(CUSTOM_THUMBNAIL_ID);
  };

  const pickedBlob =
    selectedId === CUSTOM_THUMBNAIL_ID ? custom?.blob : candidates.find((c) => c.id === selectedId)?.blob;
  const coverProblem = youtube && pickedBlob ? youtubeCoverProblem(pickedBlob) : null;

  const commit = async () => {
    const selectedBlob = pickedBlob;
    if (!selectedBlob || coverProblem) return;
    setSaving(true);
    try {
      const thumbTicket = await thumbUploadUrl.mutateAsync({
        mimetype: selectedBlob.type || "image/jpeg",
        extension: thumbnailExtension(
          selectedBlob,
          custom?.blob instanceof File ? custom.blob.name : undefined,
        ),
        size: selectedBlob.size,
      });
      await putToPresignedUrl(
        thumbTicket.uploadUrl,
        selectedBlob,
        selectedBlob.type || "image/jpeg",
      );
      await setThumbnail.mutateAsync({ thumbnailToken: thumbTicket.thumbnailToken, deliverableId });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={hasThumbnail ? "Change thumbnail" : "Add thumbnail"}
      description="Pick a frame or upload an image. This doesn't re-upload the video."
      modalClassName="max-w-[640px]"
    >
      <div className="flex flex-col gap-4">
        <ThumbnailPicker
          candidates={candidates}
          custom={custom ? { previewUrl: custom.previewUrl } : null}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCustomFile={onCustomFile}
          captureState={captureState}
          disabled={saving}
          youtube={youtube}
          problem={coverProblem}
        />
        <div className="flex items-center justify-end gap-2 border-t border-co-line pt-4">
          <KitButton variant="ghost" onClick={onClose} isDisabled={saving}>
            Cancel
          </KitButton>
          <KitButton loading={saving} isDisabled={!selectedId || !!coverProblem} onClick={() => void commit()}>
            Use this thumbnail
          </KitButton>
        </div>
      </div>
    </KitModal>
  );
}

// ---- Add / remove platforms ----

/**
 * The platforms a slot targets: its own list for a quick post, the signed-off
 * idea's for a pipeline one. The server routes the write the same way.
 */
function slotChannelsOf(slot: CalendarPlanSlot): CalendarPlatform[] {
  return intendedPublicationChannels(slot);
}

type PublicationCountSummary = {
  pending: number;
  inFlight: number;
  retrying: number;
  scheduled: number;
  live: number;
  failed: number;
  blocked: number;
  manual: number;
};

/** Every intended target lands in exactly one summary bucket. Publication rows
 * outside that set are audit history and never inflate the current result. */
function publicationCounts(
  targets: CalendarPlatform[],
  publications: Publication[],
  skipped: SkippedChannel[],
): PublicationCountSummary {
  const counts: PublicationCountSummary = {
    pending: 0,
    inFlight: 0,
    retrying: 0,
    scheduled: 0,
    live: 0,
    failed: 0,
    blocked: 0,
    manual: 0,
  };
  const publicationByPlatform = new Map(
    publications.map((publication) => [publication.platform, publication]),
  );
  const skippedByPlatform = new Map(skipped.map((entry) => [entry.platform, entry]));

  for (const platform of targets) {
    const publication = publicationByPlatform.get(platform);
    if (publication) {
      switch (publication.status) {
        case "PUBLISHED":
          counts.live += 1;
          break;
        case "FAILED":
          if (publication.nextAttemptAt) counts.retrying += 1;
          else counts.failed += 1;
          break;
        case "PUBLISHING":
          counts.inFlight += 1;
          break;
        case "SCHEDULED":
          counts.scheduled += 1;
          break;
        case "DRAFT":
        case "READY":
        case "CANCELLED":
          counts.pending += 1;
          break;
      }
      continue;
    }

    const reason = skippedByPlatform.get(platform)?.reason;
    if (reason === "NOT_PUBLISHABLE") counts.manual += 1;
    else if (reason === "NO_ACCOUNT" || reason === "NO_FILE") counts.blocked += 1;
    else counts.pending += 1;
  }

  return counts;
}

export function EditPlatformsModal({
  slot,
  brandId,
  onClose,
}: {
  slot: CalendarPlanSlot;
  brandId: string;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<CalendarPlatform[]>(() => slotChannelsOf(slot));
  const updateSlot = useUpdatePlanSlot(brandId, slot.planId);

  const save = () =>
    updateSlot.mutate(
      { slotId: slot.id, slot: { channels: draft } },
      { onSuccess: onClose },
    );

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Platforms"
      description="Where this post goes out. Removing a platform cancels its scheduled publish; one already live stays up."
      modalClassName="max-w-[480px]"
    >
      <div className="flex flex-col gap-4">
        <PlatformPicker value={draft} onChange={setDraft} />
        <div className="flex items-center justify-end gap-2 border-t border-co-line pt-4">
          <KitButton variant="ghost" onClick={onClose} isDisabled={updateSlot.isPending}>
            Cancel
          </KitButton>
          <KitButton loading={updateSlot.isPending} onClick={save}>
            Save platforms
          </KitButton>
        </div>
      </div>
    </KitModal>
  );
}

// ---- The panel ----

export default function DistributionPanel({
  slot,
  brief,
  brandId,
  timezone,
  timezoneReady,
  timezoneError,
  canEdit,
  pillarHue,
}: {
  slot: CalendarPlanSlot;
  /** Null on a DIRECT quick post: there is no brief, and none is needed. */
  brief: ProductionBrief | null;
  brandId: string;
  timezone: string;
  timezoneReady: boolean;
  timezoneError: boolean;
  canEdit: boolean;
  pillarHue?: string;
}) {
  // The one gate, matching the server's: an accepted brief, or a quick post.
  const schedulable = brief === null || brief.status === "ACCEPTED";
  const distribution = useDistribution(slot.id, schedulable);
  const accounts = usePublishableAccounts(brandId, schedulable);
  const removeFile = useDeleteSlotFile(slot.id);
  const [activeId, setActiveId] = useState<string | null>(null);
  const data: DistributionResult | undefined = distribution.data;
  const files = useMemo(() => distributionFiles(data), [data]);
  const hasFiles = files.length > 0;

  const [thumbFor, setThumbFor] = useState<{ fileId: string; mediaUrl: string | null } | null>(null);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const thumbFile = thumbFor ? files.find((file) => file.id === thumbFor.fileId && file.kind === "VIDEO") : undefined;

  const allPublications = useMemo(() => distributionPublications(data), [data]);
  const sorted = useMemo(
    () => [...allPublications].sort((a, b) => a.platform.localeCompare(b.platform)),
    [allPublications],
  );

  const targetChannels = useMemo(() => slotChannelsOf(slot), [slot]);
  // The platforms a new file can be for: the read's list, else the slot's own.
  const uploadChannels = data?.channels ?? targetChannels;
  const targetSet = useMemo(() => new Set<CalendarPlatform>(targetChannels), [targetChannels]);
  const currentPublications = useMemo(
    () => sorted.filter((publication) => targetSet.has(publication.platform)),
    [sorted, targetSet],
  );
  const historicalPublications = useMemo(
    () => sorted.filter((publication) => !targetSet.has(publication.platform)),
    [sorted, targetSet],
  );
  const currentSkipped = useMemo(() => {
    const fromRead = (data?.skippedChannels ?? []).filter((entry) =>
      targetSet.has(entry.platform),
    );
    const represented = new Set<CalendarPlatform>([
      ...currentPublications.map((publication) => publication.platform),
      ...fromRead.map((entry) => entry.platform),
    ]);
    const missing = targetChannels
      .filter((platform) => !represented.has(platform))
      .map((platform): SkippedChannel => ({ platform, reason: "NEEDS_FANOUT" }));
    return [...fromRead, ...missing];
  }, [currentPublications, data?.skippedChannels, targetChannels, targetSet]);

  const manualChannels = useMemo(
    () => currentSkipped.filter((s) => s.reason === "NOT_PUBLISHABLE"),
    [currentSkipped],
  );
  const needsFile = useMemo(
    () => currentSkipped.filter((s) => s.reason === "NO_FILE"),
    [currentSkipped],
  );
  // A platform whose file is still being checked has no publication yet
  // (NEEDS_FANOUT); it gets one when the file passes, so nothing to create.
  const checkingFor = useMemo(() => {
    const checking = files.filter(
      (file) => isPendingSlotFile(file) || file.status === "UPLOADING" || file.status === "PROCESSING",
    );
    return new Set(
      targetChannels.filter((platform) =>
        checking.some((file) => file.forPlatform === platform || file.forPlatform === null),
      ),
    );
  }, [files, targetChannels]);
  const waitingOnFile = useMemo(
    () => currentSkipped.filter((s) => s.reason === "NEEDS_FANOUT" && checkingFor.has(s.platform)),
    [currentSkipped, checkingFor],
  );
  const skippedNoAccount = useMemo(
    () =>
      currentSkipped.filter(
        (s) =>
          s.reason !== "NOT_PUBLISHABLE" &&
          s.reason !== "NO_FILE" &&
          !(s.reason === "NEEDS_FANOUT" && checkingFor.has(s.platform)),
      ),
    [currentSkipped, checkingFor],
  );

  useEffect(() => {
    const ids = new Set(currentPublications.map((p) => p.id));
    if (activeId && !ids.has(activeId)) {
      // Drop a tab that vanished (publication deleted / channel list changed).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveId(null);
    }
  }, [currentPublications, activeId]);

  const activeTabId = activeId ?? currentPublications[0]?.id ?? null;

  if (!schedulable) return null;

  const counts = publicationCounts(targetChannels, currentPublications, currentSkipped);

  // The server decides: it refuses (409, with the reason) while a scheduled or
  // posted publication uses the file, and the file comes back.
  const onRemoveFile = (file: SlotFile) => {
    if (!window.confirm(`Remove ${file.fileName}? Platforms that use it will need another file.`)) return;
    removeFile.mutate(file.id);
  };

  return (
    <div className="flex flex-col gap-9 pb-2">
      {timezone === "UTC" && timezoneReady && (
        <div role="status" className="tt-read-s flex items-start gap-2 border-l-2 border-amber-500 bg-amber-500/10 px-3 py-3 text-amber-700">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            This brand is still on the UTC default. Set the timezone in Calendar settings before scheduling so posts go out at the wall-clock you expect.
          </span>
        </div>
      )}

      {distribution.isError ? (
        <section role="alert" className="border border-red-500/30 bg-red-500/5 px-4 py-5">
          <h4 className="tt-title-m font-extrabold uppercase text-co-text">Publishing details unavailable</h4>
          <p className="tt-read-m mt-1 text-co-muted">
            We could not load this post’s asset or destinations. Nothing has been replaced.
          </p>
          <button
            type="button"
            onClick={() => void distribution.refetch()}
            className="tt-label mt-4 inline-flex min-h-11 items-center gap-2 border border-co-line2 px-4 uppercase text-co-text transition hover:border-co-accent"
          >
            <RefreshCw className="size-3.5" /> Retry
          </button>
        </section>
      ) : (
        <>
          {hasFiles && targetChannels.length > 0 ? <PublicationCounts {...counts} /> : null}

          <section aria-labelledby={`post-files-${slot.id}`} className="flex flex-col gap-4">
            <SectionHeading
              id={`post-files-${slot.id}`}
              step="01"
              title="Files"
              description="One file for every platform, or a different file per platform. Each destination below publishes the file it uses."
              action={
                canEdit && !hasFiles && !distribution.isLoading ? (
                  <button
                    type="button"
                    onClick={() => setPlatformsOpen(true)}
                    className="tt-label inline-flex min-h-11 items-center gap-2 border border-co-line2 px-4 uppercase text-co-text2 transition hover:border-co-accent"
                  >
                    <Pencil className="size-3.5" /> Edit platforms
                  </button>
                ) : null
              }
            />

            {distribution.isLoading ? (
              <FilesSkeleton />
            ) : !hasFiles ? (
              canEdit ? (
                <AddFile slot={slot} channels={uploadChannels} onAddPlatform={() => setPlatformsOpen(true)} />
              ) : (
                <div className="border border-dashed border-co-line2 bg-co-recess px-4 py-8 text-center">
                  <p className="tt-read-m text-co-faint">No file has been uploaded yet.</p>
                </div>
              )
            ) : (
              <>
                <ul aria-label="The post's files" className="border border-co-line2 bg-co-surface">
                  {files.map((file) => (
                    <FileRow
                      key={file.id}
                      file={file}
                      usedBy={currentPublications
                        .filter((publication) => publication.deliverableId === file.id)
                        .map((publication) => publication.platform)}
                      canEdit={canEdit}
                      initiallyOpen={files.length === 1}
                      onRemove={() => onRemoveFile(file)}
                      onChangeThumbnail={(mediaUrl) => setThumbFor({ fileId: file.id, mediaUrl })}
                      onRefresh={() => void distribution.refetch()}
                    />
                  ))}
                </ul>
                {canEdit ? (
                  <div className="flex flex-col gap-3 border-t border-co-line pt-4">
                    <h5 className="tt-section font-bold uppercase text-co-text">Add a file</h5>
                    <AddFile slot={slot} channels={uploadChannels} onAddPlatform={() => setPlatformsOpen(true)} />
                  </div>
                ) : null}
              </>
            )}
          </section>

          {hasFiles ? (
            <section aria-labelledby={`distribution-${slot.id}`} className="flex flex-col gap-4">
              <SectionHeading
                id={`distribution-${slot.id}`}
                step="02"
                title="Distribution"
                description="Each destination publishes its file with its own copy, account and timing. Pick a channel to edit it."
                action={
                  canEdit ? (
                    <button
                      type="button"
                      onClick={() => setPlatformsOpen(true)}
                      className="tt-label inline-flex min-h-11 items-center gap-2 border border-co-line2 px-4 uppercase text-co-text2 transition hover:border-co-accent"
                    >
                      <Pencil className="size-3.5" /> Edit platforms
                    </button>
                  ) : null
                }
              />

              {accounts.isError ? (
                <div role="alert" className="tt-read-s flex flex-wrap items-center justify-between gap-3 border border-red-500/30 bg-red-500/5 px-3 py-3 text-red-500">
                  <span>Connected publishing accounts could not be loaded. Scheduling is paused until this is refreshed.</span>
                  <button
                    type="button"
                    onClick={() => void accounts.refetch()}
                    className="tt-label inline-flex min-h-11 items-center border border-red-500/30 px-3 uppercase"
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              {targetChannels.length === 0 ? (
                <div className="border border-dashed border-co-line2 bg-co-recess px-4 py-7 text-center">
                  <p className="tt-read-m text-co-muted">No destinations have been added to this post.</p>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => setPlatformsOpen(true)}
                      className="tt-label mt-3 inline-flex min-h-11 items-center gap-2 border border-co-line2 px-4 uppercase text-co-text transition hover:border-co-accent"
                    >
                      <Plus className="size-3.5" /> Add a platform
                    </button>
                  ) : null}
                </div>
              ) : null}

              {currentPublications.length > 0 ? (
                <>
                  <nav aria-label="Publishing destinations" className="overflow-x-auto border-y border-co-line2 bg-co-surface">
                    <div className="grid min-w-max auto-cols-[minmax(14rem,1fr)] grid-flow-col">
                      {currentPublications.map((publication) => {
                        const meta = PLATFORM_META[publication.platform];
                        const active = publication.id === activeTabId;
                        const listedAccount = (accounts.data ?? []).find(
                          (account) => account.accountId === publication.socialAccountId,
                        );
                        return (
                          <button
                            key={publication.id}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setActiveId(publication.id)}
                            className={cn(
                              "flex min-h-[82px] min-w-0 flex-col justify-between gap-2 border-r border-t px-4 py-3 text-left transition-colors last:border-r-0",
                              active
                                ? "border-t-co-accent bg-co-accent-dim text-co-text"
                                : "border-t-transparent bg-co-surface text-co-muted hover:bg-co-recess hover:text-co-text",
                            )}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <PlatformGlyph platform={publication.platform} className="size-4 shrink-0" />
                              <span className="tt-section truncate font-bold uppercase">{meta.label}</span>
                            </span>
                            <span className="flex items-center justify-between gap-3">
                              <span className="tt-label truncate uppercase text-co-faint">
                                {listedAccount?.name ?? listedAccount?.username ?? publication.accountName ?? "Account pending"}
                              </span>
                              <WorkflowTag state={publicationWorkflowState(publication.status, publication.nextAttemptAt)}>
                                {publicationStatusLabel(publication)}
                              </WorkflowTag>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </nav>

                  {(() => {
                    const active = currentPublications.find((publication) => publication.id === activeTabId);
                    if (!active) return null;
                    return (
                      <PublicationCard
                        key={`${active.id}:${timezone}:${timezoneReady}`}
                        publication={active}
                        slot={slot}
                        timezone={timezone}
                        timezoneReady={timezoneReady}
                        timezoneError={timezoneError}
                        slotDay={data?.slotDay ?? slot.scheduledAt}
                        accounts={accounts.data ?? []}
                        accountsLoading={accounts.isLoading}
                        canEdit={canEdit}
                        pillarHue={pillarHue}
                        files={files}
                        coverLock={data?.coverLocks?.find((lock) => lock.platform === active.platform)?.reason ?? null}
                      />
                    );
                  })()}
                </>
              ) : null}

              {waitingOnFile.length > 0 ? (
                <div className="border border-co-line2 bg-co-surface">
                  <div className="border-b border-co-line2 bg-co-recess px-4 py-3">
                    <h5 className="tt-section font-bold uppercase text-co-text">Checking the file</h5>
                    <p className="tt-read-s mt-0.5 text-co-muted">
                      Each of these gets its publication once its file passes the platform checks.
                    </p>
                  </div>
                  {waitingOnFile.map((skipped) => (
                    <SkippedCard key={skipped.platform} skipped={skipped} slotId={slot.id} canEdit={canEdit} checking />
                  ))}
                </div>
              ) : null}

              {needsFile.length > 0 ? (
                <div className="border border-co-line2 bg-co-surface">
                  <div className="border-b border-co-line2 bg-co-recess px-4 py-3">
                    <h5 className="tt-section font-bold uppercase text-co-text">Needs a file</h5>
                    <p className="tt-read-s mt-0.5 text-co-muted">
                      These destinations have no file of their own and there is no file for every platform.
                    </p>
                  </div>
                  {needsFile.map((skipped) => (
                    <NeedsFileRow key={skipped.platform} platform={skipped.platform} slot={slot} canEdit={canEdit} />
                  ))}
                </div>
              ) : null}

              {skippedNoAccount.length > 0 ? (
                <div className="border border-co-line2 bg-co-surface">
                  <div className="border-b border-co-line2 bg-co-recess px-4 py-3">
                    <h5 className="tt-section font-bold uppercase text-co-text">Connection setup</h5>
                    <p className="tt-read-s mt-0.5 text-co-muted">These destinations need a publication or a connected account.</p>
                  </div>
                  {skippedNoAccount.map((skipped) => (
                    <SkippedCard key={skipped.platform} skipped={skipped} slotId={slot.id} canEdit={canEdit} />
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {hasFiles && manualChannels.length > 0 ? (
            <section aria-labelledby={`manual-publishing-${slot.id}`} className="flex flex-col gap-4">
              <SectionHeading
                id={`manual-publishing-${slot.id}`}
                step="03"
                title="Manual posting"
                description="These destinations cannot be auto-published. They stay outstanding until handled outside Creative OS."
                action={(
                  <WorkflowTag state="outstanding">
                    {manualChannels.length} outstanding
                  </WorkflowTag>
                )}
              />
              <div className="border border-co-line2 bg-co-surface">
                {manualChannels.map((skipped) => (
                  <SkippedCard key={skipped.platform} skipped={skipped} slotId={slot.id} canEdit={canEdit} />
                ))}
              </div>
              <p className="tt-read-s text-co-faint">Manual completion tracking is not available yet.</p>
            </section>
          ) : null}

          {historicalPublications.length > 0 ? (
            <details className="group border-y border-co-line2 bg-co-surface">
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3 py-3 marker:hidden sm:px-4">
                <span>
                  <span className="tt-section block font-bold uppercase text-co-text">Previous channel history</span>
                  <span className="tt-read-s mt-0.5 block text-co-muted">Removed from the current plan and excluded from the summary.</span>
                </span>
                <span className="tt-label shrink-0 uppercase text-co-faint group-open:text-co-accent">
                  {historicalPublications.length} records
                </span>
              </summary>
              <ul className="border-t border-co-line2">
                {historicalPublications.map((publication) => (
                  <li key={publication.id} className="flex min-h-11 flex-wrap items-center justify-between gap-3 border-b border-co-line px-3 py-2.5 last:border-b-0 sm:px-4">
                    <span className="tt-read-s inline-flex items-center gap-2 font-medium text-co-text2">
                      <PlatformGlyph platform={publication.platform} className="size-3.5" />
                      {PLATFORM_META[publication.platform].label}
                    </span>
                    <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      <WorkflowTag state={publicationWorkflowState(publication.status, publication.nextAttemptAt)}>
                        {publicationStatusLabel(publication)}
                      </WorkflowTag>
                      {publication.publishedAt ? (
                        <span className="tt-label text-co-faint">{fmtBrandTimestamp(publication.publishedAt, timezone)}</span>
                      ) : null}
                      {publication.permalink ? (
                        <a href={publication.permalink} target="_blank" rel="noreferrer" className="tt-label inline-flex min-h-11 items-center gap-1 uppercase text-co-accent underline underline-offset-4">
                          <Link2 className="size-3" /> View post
                        </a>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      )}
      {thumbFor && thumbFile ? (
        <ChangeThumbnailModal
          slotId={slot.id}
          deliverableId={thumbFile.id}
          mediaUrl={thumbFor.mediaUrl}
          hasThumbnail={!!thumbFile.thumbnailUrl}
          youtube={fileReachesYouTube(thumbFile.forPlatform, uploadChannels)}
          onClose={() => setThumbFor(null)}
        />
      ) : null}
      {platformsOpen && (
        <EditPlatformsModal slot={slot} brandId={brandId} onClose={() => setPlatformsOpen(false)} />
      )}
    </div>
  );
}
